import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import "./application-frame.css";
import isaraMarkUrl from "./isara-mark.svg";

export interface FrameNavigationItem {
  to: string;
  label: string;
  hint?: string;
  end?: boolean;
  activeWhen?: (pathname: string) => boolean;
}

interface ApplicationFrameProps {
  variant: "operations" | "analyst";
  brand: { to: string; label: string; title: string; subtitle?: string };
  brandStatus?: ReactNode;
  navigation: readonly FrameNavigationItem[];
  secondaryNavigation?: readonly FrameNavigationItem[];
  action?: ReactNode;
  footer: ReactNode;
  contentClassName?: string;
  children?: ReactNode;
}

const SIDEBAR_HIDDEN_STORAGE_KEY = "fdr-e2e-agent-sidebar-hidden";

function initialSidebarHidden(): boolean {
  if (typeof window === "undefined") return false;
  if (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 720px)").matches
  ) {
    return true;
  }
  try {
    return window.localStorage.getItem(SIDEBAR_HIDDEN_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Shared application chrome and visual language for both role-selected
 * workspaces. Only the navigation, actions, and routed content differ.
 */
export default function ApplicationFrame({
  variant,
  brand,
  brandStatus,
  navigation,
  secondaryNavigation = [],
  action,
  footer,
  contentClassName,
  children,
}: ApplicationFrameProps) {
  const location = useLocation();
  const [sidebarHidden, setSidebarHidden] = useState(initialSidebarHidden);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_HIDDEN_STORAGE_KEY,
        String(sidebarHidden),
      );
    } catch {
      // The frame still works when browser storage is blocked.
    }
  }, [sidebarHidden]);

  return (
    <div
      className={[
        "workspace-shell",
        `workspace-shell--${variant}`,
        sidebarHidden ? "workspace-shell--sidebar-hidden" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <a className="workspace-skip-link" href="#main-content">
        Skip to content
      </a>
      <aside
        id="workspace-sidebar"
        className={[
          "workspace-sidebar",
          sidebarHidden ? "workspace-sidebar--hidden" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className="workspace-sidebar__toggle"
          aria-controls="workspace-sidebar-content"
          aria-expanded={!sidebarHidden}
          aria-label={sidebarHidden ? "Show sidebar" : "Hide sidebar"}
          title={sidebarHidden ? "Show sidebar" : "Hide sidebar"}
          onClick={() => setSidebarHidden((hidden) => !hidden)}
        >
          <span aria-hidden="true">{sidebarHidden ? "›" : "‹"}</span>
        </button>
        <div
          id="workspace-sidebar-content"
          className="workspace-sidebar__content"
          hidden={sidebarHidden}
        >
          <NavLink
            className="workspace-brand"
            to={brand.to}
            aria-label={brand.label}
          >
            <img
              className="workspace-brand__mark"
              src={isaraMarkUrl}
              alt=""
              aria-hidden="true"
            />
            <span className="workspace-brand__copy">
              <span className="workspace-brand__title-row">
                <strong>{brand.title}</strong>
                {brandStatus}
              </span>
              {brand.subtitle && <small>{brand.subtitle}</small>}
            </span>
          </NavLink>
          <nav className="workspace-nav" aria-label="Primary navigation">
            {navigation.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                aria-current={
                  item.activeWhen?.(location.pathname) ? "page" : undefined
                }
                className={({ isActive }) => {
                  const active =
                    isActive || Boolean(item.activeWhen?.(location.pathname));
                  return ["workspace-nav__item", active ? "active" : ""]
                    .filter(Boolean)
                    .join(" ");
                }}
              >
                <span>{item.label}</span>
                {item.hint && <small>{item.hint}</small>}
              </NavLink>
            ))}
          </nav>
          {action && <div className="workspace-sidebar__action">{action}</div>}
          {secondaryNavigation.length > 0 && (
            <nav
              className="workspace-nav workspace-nav--secondary"
              aria-label="Settings navigation"
            >
              {secondaryNavigation.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  aria-current={
                    item.activeWhen?.(location.pathname) ? "page" : undefined
                  }
                  className={({ isActive }) => {
                    const active =
                      isActive || Boolean(item.activeWhen?.(location.pathname));
                    return ["workspace-nav__item", active ? "active" : ""]
                      .filter(Boolean)
                      .join(" ");
                  }}
                >
                  <span>{item.label}</span>
                  {item.hint && <small>{item.hint}</small>}
                </NavLink>
              ))}
            </nav>
          )}
          <div className="workspace-sidebar__footer">{footer}</div>
        </div>
      </aside>
      <main className="workspace-main" id="main-content" tabIndex={-1}>
        <div
          className={["workspace-content", contentClassName]
            .filter(Boolean)
            .join(" ")}
        >
          {children ?? <Outlet />}
        </div>
      </main>
    </div>
  );
}
